import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import {
  USUARIOS_RENTAS_ITEMS,
  HAMBURGER_NAVIGATION,
  ENTITLEMENT_MODULE_ALIASES,
  FIXED_SIDEBAR_ITEM_KEYS,
  type NavigationItem,
} from '../config/navigation';

// (2026-09-07 v3) Matriz de permisos en vivo — /admin/entitlements-matrix
// MULTI-ROLE REWORK per Jaime's ruling: a person can hold DIFFERENT roles in
// DIFFERENT contexts (e.g. Empleado of TH1 and Propietario/Residente of Casa
// Prueba). Verified: Emily = Residente (Allegra 601, TH1) +
// Propietario/Residente (Casa Tajali); Francisco = Empleado (TH1) +
// Propietario/Residente (Casa Prueba).
//
// Contract: GET /api/admin/entitlements/effective-matrix returns
//   users[] each with contexts[] { contextLabel, role, modules, features,
//   limits, sourceAttribution }.
// PAID/FREE SPLIT (2026-09-07, Jaime's ruling): the role columns are split by
// plan dimension → Módulo | Super-admin | Admin | Propietario·Pago |
// Propietario·Gratis | Residente·Pago | Residente·Gratis | Empleado·Pago |
// Empleado·Gratis. Each context routes to exactly ONE column: its role + its
// paid flag. paid = any paid dimension: home_plus / home_ultra / paid-condo
// superset (condo_small..condo_xxl, i.e. condo_* that is not condo_free) /
// supplier_pro. The backend is adding an explicit `paid: boolean` per context;
// until it is redeployed we derive it client-side from sourceAttribution /
// plan.source with the same rule, and prefer the backend field once it lands.
//
// DUAL-ROLE FANOUT (2026-09-07, Jaime's 15:12 UTC ruling): the backend emits a
// `roles[]` array per context (scalar `role` is just precedence: empleado >
// propietario > residente > admin). A multi-role context (e.g. Francisco on
// casa prueba = propietario+residente) is now emitted into EVERY matching
// role column (Propietario·Pago/Gratis AND Residente·Pago/Gratis) with the
// SAME mask/limits/paid flag. Super-admin/admin columns keep scalar behavior.
// Column subtitles count context-contributions per column (a dual-role
// context counts once in each of its columns).

// With one context per column, cells collapse back to SINGLE glyphs
// (✅/❌/🔸N). The split-badge path is kept only as a fallback for residual
// disagreement (it should rarely fire now). Column subtitle shows
// "N contexto(s)"; persons shown too when they differ.
//
// Drill-down: click a column → persons of that role; click a person → their
// contexts side by side with effective modules + source attribution. The old
// empleado⇄propietario toggle is superseded by the per-person contexts view.
//
// Super-admin column: full access by design (noted in tooltip).
// The only fallback is the explicit "Datos de ejemplo" toggle (clearly badged).

// ---------- Types (backend contract) ----------
type MatrixContext = {
  contextLabel?: string | null;
  mode?: string | null;
  condoName?: string | null;
  unitNumber?: string | null;
  role?: string | null;
  roles?: string[] | null; // full role set for this context (backend emits it)
  modules?: string[] | null;
  features?: string[] | null;
  limits?: Record<string, unknown> | null;
  sourceAttribution?: unknown;
  paid?: boolean; // backend flag (when redeployed); client derives it meanwhile
  // F-12 (2026-09-25): payer attribution per context (backend admin-matrix-routes.js).
  // coveredByAncestor=true ⇒ this context's plan is covered by an ancestor condo.
  payerInfo?: { payerCondoId?: string | null; payerPlan?: string | null; depth?: number | null; coveredByAncestor?: boolean; relationship?: 'self' | 'ancestor' } | null;
};
type MatrixEntry = {
  userId?: string;
  id?: string;
  name?: string | null;
  email?: string | null;
  role?: string | null;
  context?: { mode?: string | null; unitNumber?: string | null; condoName?: string | null };
  plan?: { plan?: string | null; tier?: string | null; source?: string | null };
  supplier?: { planId?: string | null; status?: string | null } | null;
  contexts?: MatrixContext[] | null;
  modules?: string[] | null;
  features?: string[] | null;
  limits?: Record<string, unknown> | null;
  employeeMasked?: boolean;
  // F-12: payer attribution on v2 per-role entries (entry-level, not per-context).
  payerInfo?: { payerCondoId?: string | null; payerPlan?: string | null; depth?: number | null; coveredByAncestor?: boolean; relationship?: 'self' | 'ancestor' } | null;
};
type MatrixResponse = {
  computed_at?: string | null;
  generated_at?: string | null;
  generatedAt?: string | null;
  users?: MatrixEntry[];
  user?: MatrixEntry;
};

const LABELS: Record<string, string> = {
  reportes: 'Finanzas', 'financial-reports': 'Reportes financieros', 'home-tasks': 'Tareas',
  'home-shopping': 'Compras', 'home-pets': 'Mascotas', 'home-vehicles': 'Vehículos',
  'home-budget': 'Presupuesto', voting: 'Votaciones', announcements: 'Anuncios',
  amenities: 'Amenidades', expenses: 'Gastos', morosos: 'Morosos', reservations: 'Reservas',
  calendar: 'Calendario', complaints: 'Quejas', regulations: 'Reglamentos', security: 'Seguridad',
  'qr-access': 'Acceso QR', panic: 'Pánico', travel: 'Viajes', qr: 'Acceso QR',
  emergency: 'Emergencias', emergencias: 'Emergencias', maintenance: 'Mantenimiento',
  usuarios: 'Usuarios', mensajes: 'Mensajes',
};

// (2026-09-07) Mis Pagos ('payroll') was REMOVED from the registry per Jaime
// (eliminated from employee view for now), so it has no matrix row — the matrix
// mirrors what the menus actually show. Only 'profile' stays excluded
// (mobile-only item, Jaime 2026-09-03 ruling).
// (2026-09-07) Rows = every item the menus can actually render. Besides the
// flat hamburger registry, the desktop "Usuarios y Rentas" group hand-builds
// three items (Usuarios/Rentas/Rotación) that are NOT in HAMBURGER_NAVIGATION —
// they were missing rows until USUARIOS_RENTAS_ITEMS was extracted in
// navigation.ts. 'profile' stays excluded (mobile-only, Jaime 2026-09-03
// ruling); 'payroll' no longer exists in the registry (Jaime 2026-09-07).
const MATRIX_COLUMNS: NavigationItem[] = [
  ...HAMBURGER_NAVIGATION.filter((item) => item.key !== 'profile'),
  ...USUARIOS_RENTAS_ITEMS,
];

// (2026-09-07, Jaime 21:19 UTC) SCOPE SECTIONS: matrix rows are visually
// divided into "Funciones de Unidad (Hogar)" vs "Funciones de Condominio",
// mirroring the resolver's scope algebra. Classification rule (documented):
//   1. planModule first - map through ENTITLEMENT_MODULE_ALIASES to the
//      BACKEND-canonical scope table MODULE_SCOPES in
//      api/src/modules/billing/plan-source.js:4-8. Unit-scope ids there:
//      home-tasks, home-shopping, home-pets, home-vehicles, home-budget,
//      panic, travel, rentals, turnover. Condo-scope: everything else
//      (communication/wall, announcements, calendar, voting, amenities,
//      expenses, morosos, regulations, reservations, security, qr-access,
//      financial-reports, ...). Unit-scope if ANY alias resolves to a
//      unit-scope backend id; otherwise condo-scope.
//   2. moduleId fallback for items with NO planModule - documented
//      UNIT_MODULE_IDS set: moduleId 'home' is the unit-scoped family module
//      (Familia y control). moduleId 'maintenance' is NOT unit: the
//      'maintenance' nav item is condo-level (Jaime 2026-08-31 comment in
//      navigation.ts); only the separate 'turnover' item is unit-scope and it
//      carries planModule 'turnover', resolved by rule 1.
//   3. Items with neither planModule nor a unit moduleId (dashboard,
//      security-lens entries, pinned ungated entries) classify as condo.
//      No platform/super-admin-only rows exist in the registry today, so no
//      third "Otras" section is emitted (fallback kept in the render loop).
const UNIT_BACKEND_FEATURE_IDS: ReadonlySet<string> = new Set([
  'home-tasks', 'home-shopping', 'home-budget', 'home-vehicles', 'home-pets',
  'panic', 'travel', 'rentals', 'turnover',
]);
const UNIT_MODULE_IDS: ReadonlySet<string> = new Set(['home']);
function scopeOf(item: NavigationItem): 'unit' | 'condo' {
  if (item.planModule) {
    const aliases = ENTITLEMENT_MODULE_ALIASES[item.planModule] || [item.planModule];
    if (aliases.some((m) => UNIT_BACKEND_FEATURE_IDS.has(m))) return 'unit';
    return 'condo';
  }
  if (item.moduleId && UNIT_MODULE_IDS.has(item.moduleId)) return 'unit';
  return 'condo';
}
const UNIT_ROWS = MATRIX_COLUMNS.filter((item) => scopeOf(item) === 'unit');
const CONDO_ROWS = MATRIX_COLUMNS.filter((item) => scopeOf(item) === 'condo');

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super-admin', admin: 'Admin', propietario: 'Propietario',
  resident: 'Residente', empleado: 'Empleado',
};
// Display order for role sets (e.g. "Propietario + Residente").
const ROLE_ORDER: Record<string, number> = {
  super_admin: 0, admin: 1, propietario: 2, resident: 3, empleado: 4,
};
const roleSetLabel = (rs: string[]): string =>
  [...rs].sort((a, b) => (ROLE_ORDER[a] ?? 9) - (ROLE_ORDER[b] ?? 9))
    .map((r) => ROLE_LABELS[r] || r).join(' + ');
// Roles that fan out into every matching column. Super-admin/admin columns
// keep scalar routing (Jaime 15:12 UTC ruling).
const FANOUT_ROLES = new Set(['propietario', 'resident', 'empleado']);

// Grid columns: role × paid. super_admin/admin are not split by plan.
type GridColumn = { key: string; label: string; role: string; paid: boolean | null; badge?: 'Pago' | 'Gratis' };
const GRID_COLUMNS: GridColumn[] = [
  { key: 'super_admin', label: 'Super-admin', role: 'super_admin', paid: null },
  { key: 'admin', label: 'Admin', role: 'admin', paid: null },
  ...(['propietario', 'resident', 'empleado'].flatMap((role): GridColumn[] => [
    { key: `${role}__paid`, label: ROLE_LABELS[role], role, paid: true, badge: 'Pago' },
    { key: `${role}__free`, label: ROLE_LABELS[role], role, paid: false, badge: 'Gratis' },
  ])),
];

// ---------- Flattening: users[] × contexts[] → per-role PersonCtx ----------
type PersonCtx = {
  personId: string;
  name: string;
  email: string;
  contextLabel: string;
  role: string; // normalized backend role key
  modules: string[];
  limits: Record<string, unknown>;
  sourceAttribution: string;
  paid: boolean;
  roleSet: string[]; // every role the person holds in this context
  // F-12: read-only payer attribution for the context row (null-safe).
  payerInfo?: { payerCondoId?: string | null; payerPlan?: string | null; depth?: number | null; coveredByAncestor?: boolean; relationship?: 'self' | 'ancestor' } | null;
};

const ROLE_ALIASES: Record<string, string> = {
  super_admin: 'super_admin', superadmin: 'super_admin', 'super-admin': 'super_admin',
  admin: 'admin', administrador: 'admin',
  propietario: 'propietario', owner: 'propietario', propietaria: 'propietario',
  resident: 'resident', residente: 'resident',
  empleado: 'empleado', employee: 'empleado',
};
const normalizeRole = (r: unknown): string => {
  const k = String(r ?? '').toLowerCase().replace(/[\s-]+/g, '_');
  return ROLE_ALIASES[k] || ROLE_ALIASES[k.replace(/_/g, '')] || '';
};

const attrToString = (a: unknown): string => {
  if (a == null) return '';
  if (typeof a === 'string') return a;
  try { return JSON.stringify(a); } catch { return String(a); }
};

const strArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((m): m is string => typeof m === 'string') : [];

const contextLabelOf = (e: MatrixEntry, c: MatrixContext | undefined, fallbackRole: string): string => {
  if (c?.contextLabel) return String(c.contextLabel);
  const parts = [c ? c.condoName ?? e.context?.condoName : undefined, c ? c.unitNumber ?? e.context?.unitNumber : undefined].filter(Boolean);
  if (parts.length > 0) return parts.join(' · ');
  return `${e.name || 'Usuario'} [${fallbackRole}]`;
};

// paid = any paid dimension: home_plus / home_ultra / supplier_pro / paid
// condo tiers (condo_small..condo_xxl — any condo_* that is not condo_free).
// Prefers the backend `paid` flag; otherwise derives from sourceAttribution /
// plan source strings. Defaults to free (unpaid) when nothing matches.
const PAID_PLAN_RE = /home_plus|home_ultra|supplier_pro|condo_(small|medium|large|xl|xxl)/;
const FREE_PLAN_RE = /(^|[^a-z_])free([^a-z_]|$)/;
function derivePaid(c: MatrixContext | undefined, e: MatrixEntry): boolean {
  if (typeof c?.paid === 'boolean') return c.paid;
  const s = [
    attrToString(c?.sourceAttribution),
    e.plan?.plan, e.plan?.tier, e.plan?.source,
  ].filter(Boolean).join(' ').toLowerCase();
  if (PAID_PLAN_RE.test(s)) return true;
  if (FREE_PLAN_RE.test(s)) return false;
  return false;
}

// Build one PersonCtx from a user entry WITHOUT contexts[] (back-compat with
// the v2 list shape: role resolved per user in their active context).
function legacyEntryToCtx(e: MatrixEntry, forcedRole?: string): PersonCtx {
  const role = normalizeRole(forcedRole ?? e.role);
  const ctx = e.context;
  return {
    personId: String(e.userId ?? e.id ?? e.email ?? e.name ?? Math.random()),
    name: e.name || '—',
    email: e.email || '',
    contextLabel: contextLabelOf(e, undefined, role),
    role,
    modules: strArray(e.modules ?? e.features),
    limits: e.limits && typeof e.limits === 'object' ? (e.limits as Record<string, unknown>) : {},
    sourceAttribution: attrToString(e.plan?.source ?? e.plan?.plan),
    paid: derivePaid(undefined, e),
    roleSet: [role],
    payerInfo: e.payerInfo ?? null,
  };
}

function flattenUsers(users: MatrixEntry[]): PersonCtx[] {
  const out: PersonCtx[] = [];
  for (const u of users) {
    const pid = String(u.userId ?? u.id ?? u.email ?? u.name ?? Math.random());
    if (Array.isArray(u.contexts) && u.contexts.length > 0) {
      for (const c of u.contexts) {
        const scalar = normalizeRole(c?.role ?? u.role);
        const roleSet = Array.from(new Set(
          ((Array.isArray(c?.roles) && c.roles.length > 0 ? c.roles : [c?.role ?? u.role]) as unknown[])
            .map(normalizeRole).filter(Boolean),
        ));
        if (roleSet.length === 0 && scalar) roleSet.push(scalar);
        // Fan out: one PersonCtx per role in the context's role set for
        // propietario/residente/empleado (same mask/limits/paid). Admin and
        // super_admin keep scalar routing only.
        const emitRoles = new Set(roleSet.filter((r) => FANOUT_ROLES.has(r)));
        if (scalar && (emitRoles.size === 0 || scalar === 'admin' || scalar === 'super_admin')) emitRoles.add(scalar);
        for (const role of emitRoles) {
          out.push({
            personId: pid,
            name: u.name || '—',
            email: u.email || '',
            contextLabel: contextLabelOf(u, c, role),
            role,
            modules: strArray(c?.modules ?? c?.features),
            limits: c?.limits && typeof c.limits === 'object' ? (c.limits as Record<string, unknown>) : {},
            sourceAttribution: attrToString(c?.sourceAttribution),
            paid: derivePaid(c, u),
            roleSet,
            payerInfo: c?.payerInfo ?? u.payerInfo ?? null,
          });
        }
      }
    } else {
      out.push(legacyEntryToCtx(u));
    }
  }
  return out;
}

// ---------- Cell logic ----------
type Status = 'visible' | 'hidden' | 'limited';
type CellMembers = { visible: string[]; limited: string[]; hidden: string[] };
type CellState =
  | { kind: 'single'; status: Status; limit?: number; readOnly?: boolean; members?: string[] }
  | { kind: 'split'; counts: { visible: number; hidden: number; limited: number; minLimit?: number; maxLimit?: number }; members?: CellMembers };

const STAFF_MANAGE_ROLES = ['super_admin', 'admin', 'propietario'];

function ctxCell(item: NavigationItem, ctx: PersonCtx): { status: Status; limit?: number; readOnly?: boolean } {
  if (FIXED_SIDEBAR_ITEM_KEYS.includes(item.key as 'mensajes' | 'complaints')) return { status: 'visible' };
  // (2026-09-07) Usuarios (staff) is role-gated in the real menus: LensDashboard
  // passes isPermitted (LensDashboard.tsx:142) which gates moduleId 'user'
  // through the admin-directory permission domain — effectively
  // super_admin/admin/propietario; resident/empleado contexts don't hold it.
  // The matrix contract has no permission snapshot, so navigation.ts mirrors it
  // with roleGate (see USUARIOS_RENTAS_ITEMS), checked here against the context
  // role set. usuariosOnly Propietario contexts carry role 'propietario', so
  // they stay ✅ exactly as the USUARIOS_ONLY_HOME_KEYS whitelist keeps
  // 'staff' visible in filterNavigation (navigation.ts USUARIOS_ONLY_HOME_KEYS).
  // (2026-09-07 21:26 UTC, Jaime ruling, supersedes the 21:14 ❌): residents
  // may VIEW other users read-only. Backend ROLES.resident grants 'user:read'
  // (src/database/user-roles.js), and roleGate now includes 'resident'
  // (navigation.ts USUARIOS_RENTAS_ITEMS). A resident-only context renders ✅
  // with a 'solo lectura' tooltip marker; propietario/admin/super_admin
  // contexts keep full-manage ✅. Empleado stays ❌ (not in roleGate).
  if (item.roleGate && !ctx.roleSet.some((r) => item.roleGate!.includes(r))) return { status: 'hidden' };
  const isReadOnlyViewer = item.key === 'staff' && !ctx.roleSet.some((r) => STAFF_MANAGE_ROLES.includes(r));
  // (2026-09-07) Rentas/Rotación: hiddenInEmployeeContext — employee contexts
  // never see them regardless of plan modules (filterNavigation chain,
  // navigation.ts: `if (item.hiddenInEmployeeContext && employeeContext) return false;`).
  if (item.hiddenInEmployeeContext && (ctx.role === 'empleado' || ctx.roleSet.includes('empleado'))) return { status: 'hidden' };
  if (!item.planModule) return isReadOnlyViewer ? { status: 'visible', readOnly: true } : { status: 'visible' };
  const accepted = ENTITLEMENT_MODULE_ALIASES[item.planModule] || [item.planModule];
  const hit = accepted.find((m) => ctx.modules.includes(m));
  if (!hit) return { status: 'hidden' };
  const limitRaw = ctx.limits[hit] ?? ctx.limits[item.planModule];
  const limitNum = typeof limitRaw === 'number' ? limitRaw : typeof limitRaw === 'string' && limitRaw !== '' ? Number(limitRaw) : NaN;
  if (Number.isFinite(limitNum) && limitNum >= 0 && limitNum !== -1) return { status: 'limited', limit: limitNum };
  return isReadOnlyViewer ? { status: 'visible', readOnly: true } : { status: 'visible' };
}

// Aggregate ALL contexts of a column. Single glyph when every context agrees
// (including the same 🔸N limit); otherwise a split badge with per-status
// counts. Never averages.
function columnCell(item: NavigationItem, ctxs: PersonCtx[]): CellState | null {
  if (ctxs.length === 0) return null;
  const cells = ctxs.map((c) => ctxCell(item, c));
  const first = cells[0];
  const sameLimit = (a: number | undefined, b: number | undefined) => a === b;
  const allAgree = cells.every((c) => c.status === first.status && sameLimit(c.limit, first.limit) && c.readOnly === first.readOnly);
  // (2026-09-07 21:26 UTC) Usuarios row: resident (view-only) member lines get
  // the '· solo lectura' suffix in tooltips.
  const memberLabel = (c: PersonCtx, readOnly?: boolean) =>
    `${c.name}${c.contextLabel ? ` — ${c.contextLabel}` : ''}${c.roleSet.length > 1 ? ` (${roleSetLabel(c.roleSet)})` : ''}${readOnly ? ' · solo lectura' : ''}`;
  if (allAgree) return {
    kind: 'single', status: first.status, limit: first.limit, readOnly: first.readOnly,
    members: ctxs.map((c, i) => memberLabel(c, cells[i].readOnly)),
  };
  const vis = cells.filter((c) => c.status === 'visible').length;
  const hid = cells.filter((c) => c.status === 'hidden').length;
  const lim = cells.filter((c): c is { status: 'limited'; limit: number } => c.status === 'limited' && typeof c.limit === 'number');
  const label = (i: number) => memberLabel(ctxs[i], cells[i].readOnly);
  const members: CellMembers = {
    visible: cells.map((c, i) => c.status === 'visible' ? label(i) : '').filter(Boolean),
    limited: cells.map((c, i) => c.status === 'limited' ? `${label(i)} (🔸${typeof c.limit === 'number' ? c.limit : '?'})` : '').filter(Boolean),
    hidden: cells.map((c, i) => c.status === 'hidden' ? label(i) : '').filter(Boolean),
  };
  return {
    kind: 'split',
    counts: {
      visible: vis, hidden: hid, limited: lim.length,
      minLimit: lim.length ? Math.min(...lim.map((c) => c.limit)) : undefined,
      maxLimit: lim.length ? Math.max(...lim.map((c) => c.limit)) : undefined,
    },
    members,
  };
}

const GLYPH: Record<Status, string> = { visible: '✅', hidden: '❌', limited: '🔸' };
const STATUS_LABEL: Record<Status, string> = { visible: 'visible', hidden: 'oculto', limited: 'con límite' };

function splitTitle(item: string, role: string, c: { visible: number; hidden: number; limited: number; minLimit?: number; maxLimit?: number }, members?: CellMembers): string {
  const parts: string[] = [];
  if (c.visible) parts.push(`${c.visible} contexto(s) visible(s)`);
  if (c.limited) parts.push(`${c.limited} con límite (🔸${c.minLimit}${c.maxLimit !== c.minLimit ? `–${c.maxLimit}` : ''})`);
  if (c.hidden) parts.push(`${c.hidden} oculto(s)`);
  const lines = [`${item} · ${role}: difiere por contexto — ${parts.join(', ')}`];
  if (members) {
    if (members.visible.length) lines.push(`✅ Incluye:\n${members.visible.join('\n')}`);
    if (members.limited.length) lines.push(`🔸 Con límite:\n${members.limited.join('\n')}`);
    if (members.hidden.length) lines.push(`❌ Excluye:\n${members.hidden.join('\n')}`);
  }
  return lines.join('\n');
}

function CellGlyph({ cell, itemLabel, roleLabel }: { cell: CellState | null; itemLabel: string; roleLabel: string }) {
  if (!cell) return <span className="text-gray-300">—</span>;
  if (cell.kind === 'single') {
    const memberList = cell.members?.length ? `\nIncluye:\n${cell.members.join('\n')}` : '';
    const readOnlyNote = cell.readOnly ? ' · solo lectura (sin edición)' : '';
    const title = cell.status === 'limited' ? `${itemLabel} · ${roleLabel}: visible con límite ${cell.limit}${readOnlyNote}${memberList}` : `${itemLabel} · ${roleLabel}: ${STATUS_LABEL[cell.status]}${readOnlyNote}${memberList}`;
    return <span title={title}>{GLYPH[cell.status]}{cell.status === 'limited' ? String(cell.limit) : ''}</span>;
  }
  const c = cell.counts;
  const parts: string[] = [];
  if (c.visible) parts.push(`✅ ${c.visible}`);
  if (c.limited) parts.push(`🔸${c.minLimit ?? "?"} ×${c.limited} ctx`);
  if (c.hidden) parts.push(`❌ ${c.hidden}`);
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-full border border-amber-300 bg-amber-50 dark:bg-transparent px-1.5 py-0.5 text-[11px] font-semibold whitespace-nowrap"
      title={splitTitle(itemLabel, roleLabel, c, cell.members)}
    >
      {parts.map((p, i) => (
        <span key={i} className={i > 0 ? 'border-l border-amber-300 pl-0.5 ml-0.5' : ''}>{p}</span>
      ))}
    </span>
  );
}

// ---------- Sample data (clearly badged, pre-backend QA only) ----------
// Mirrors the verified multi-role examples: Emily (Residente Allegra 601 +
// TH1, Propietario/Residente Casa Tajali) and Francisco (Empleado TH1,
// Propietario/Residente Casa Prueba).
const FULL_MODULES = ['reportes', 'expenses', 'morosos', 'announcements', 'amenities', 'calendar', 'voting', 'regulations', 'reservations', 'panic', 'travel', 'qr-access', 'security', 'maintenance', 'usuarios', 'mensajes', 'complaints'];
const HOME_MODULES = ['home-tasks', 'home-shopping', 'home-budget', 'home-pets', 'home-vehicles', 'complaints', 'mensajes', 'reservations'];
const SAMPLE_USERS: MatrixEntry[] = [
  { userId: 's1', name: 'Sofía Super', email: 'super@demo.mx', contexts: [
    { contextLabel: 'Global (todas las cuentas)', role: 'super_admin', modules: FULL_MODULES, limits: {}, sourceAttribution: 'super_admin: acceso completo por diseño' },
  ] },
  { userId: 'a1', name: 'Bruno Díaz', email: 'bruno@demo.mx', contexts: [
    { contextLabel: 'Torres del Parque', role: 'admin', modules: ['reportes', 'expenses', 'morosos', 'announcements', 'amenities', 'calendar', 'voting', 'complaints', 'regulations', 'panic', 'travel', 'qr-access', 'maintenance', 'usuarios', 'mensajes'], limits: { reservations: 4 }, sourceAttribution: 'plan condominio: condo_medium' },
  ] },
  { userId: 'em1', name: 'Emily Demo', email: 'emily@demo.mx', contexts: [
    { contextLabel: 'Allegra 601', role: 'resident', modules: ['home-tasks', 'home-shopping', 'complaints', 'mensajes', 'announcements'], limits: {}, sourceAttribution: 'plan condominio: free (Allegra)' },
    { contextLabel: 'TH1', role: 'resident', modules: ['home-tasks', 'home-shopping', 'home-budget', 'complaints', 'mensajes', 'reservations', 'amenities'], limits: { reservations: 2 }, sourceAttribution: 'plan condominio: home_plus (TH1)' },
    // Multi-role context (fanout: appears in Propietario·Pago AND Residente·Pago)
    { contextLabel: 'Casa Tajali', role: 'propietario', roles: ['propietario', 'resident'], modules: [...HOME_MODULES, 'reportes', 'expenses', 'qr-access', 'panic'], limits: { reservations: -1 }, sourceAttribution: 'suscripción unidad: home_ultra (Casa Tajali)' },
  ] },
  { userId: 'fr1', name: 'Francisco Demo', email: 'francisco@demo.mx', contexts: [
    { contextLabel: 'TH1 (empleado)', role: 'empleado', modules: ['complaints', 'mensajes', 'qr-access', 'panic'], limits: {}, sourceAttribution: 'rol empleado (mask) en TH1' },
    // Multi-role context (fanout: appears in Propietario·Pago AND Residente·Pago)
    { contextLabel: 'Casa Prueba', role: 'propietario', roles: ['propietario', 'resident'], modules: [...HOME_MODULES, 'expenses'], limits: { reservations: 3 }, sourceAttribution: 'plan condominio: home_plus (Casa Prueba)' },
  ] },
  { userId: 'r1', name: 'Carla Ruiz', email: 'carla@demo.mx', contexts: [
    { contextLabel: 'Las Palmas · 3-A', role: 'resident', modules: ['home-tasks', 'home-shopping', 'complaints', 'mensajes'], limits: {}, sourceAttribution: 'plan condominio: free (Las Palmas)' },
  ] },
];

// ---------- Component ----------
type ColumnState = { loading: boolean; error: string; ctxs: PersonCtx[]; persons: number };

const EMPTY_COLUMN: ColumnState = { loading: true, error: '', ctxs: [], persons: 0 };

export default function EntitlementsMatrixPage() {
  const [computedAt, setComputedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usingSample, setUsingSample] = useState(false);
  const [columnState, setColumnState] = useState<Record<string, ColumnState>>(() =>
    Object.fromEntries(GRID_COLUMNS.map((c) => [c.key, { ...EMPTY_COLUMN }])));
  const [drillRole, setDrillRole] = useState<string | null>(null);
  const [drillPersonId, setDrillPersonId] = useState<string | null>(null);

  const applyCtxs = useCallback((ctxs: PersonCtx[]) => {
    // Each context routes to exactly ONE column: its role + its paid flag.
    setColumnState(Object.fromEntries(GRID_COLUMNS.map((col) => {
      const ctxsForCol = ctxs.filter((c) => c.role === col.role && (col.paid === null || c.paid === col.paid));
      return [col.key, { loading: false, error: '', ctxs: ctxsForCol, persons: new Set(ctxsForCol.map((c) => c.personId)).size }];
    })));
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    setUsingSample(false);
    setColumnState(Object.fromEntries(GRID_COLUMNS.map((c) => [c.key, { ...EMPTY_COLUMN }])));
    setComputedAt(new Date().toISOString());
    try {
      // New contract: one fetch returns users[] each with contexts[] (all roles).
      const res = await api.get('/admin/entitlements/effective-matrix', { params: { limit: 200 } });
      const data: MatrixResponse = res.data?.data ?? res.data ?? {};
      const users = Array.isArray(data.users) ? data.users : [];
      const ctxs = flattenUsers(users);
      if (ctxs.length === 0) throw new Error('empty');
      applyCtxs(ctxs);
    } catch {
      // Fallback: v2 per-role fetches (entries lack contexts[]; each entry is
      // one context of the requested role). List mode has no limits → limited
      // cells may render ✅ instead of 🔸N until the drilldown contract lands.
      try {
        const gridRoles = Array.from(new Set(GRID_COLUMNS.map((c) => c.role)));
        const results = await Promise.all(gridRoles.map(async (r): Promise<PersonCtx[]> => {
          try {
            const res = await api.get('/admin/entitlements/effective-matrix', { params: { role: r, limit: 200 } });
            const data: MatrixResponse = res.data?.data ?? res.data ?? {};
            const users = Array.isArray(data.users) ? data.users : [];
            return flattenUsers(users).map((c) => ({ ...c, role: r }));
          } catch { return []; }
        }));
        const ctxs = results.flat();
        if (ctxs.length === 0) throw new Error('empty');
        applyCtxs(ctxs);
      } catch {
        setError('No se pudo cargar la matriz efectiva (el endpoint /admin/entitlements/effective-matrix aún no responde).');
        setComputedAt(null);
      }
    }
    setLoading(false);
  }, [applyCtxs]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadSample = () => {
    setUsingSample(true);
    setError('');
    setComputedAt(new Date().toISOString());
    applyCtxs(flattenUsers(SAMPLE_USERS));
  };

  const fmtTime = (iso: string | null) => {
    if (!iso) return '—';
    try { return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'medium' }).format(new Date(iso)); } catch { return iso; }
  };

  // Persons visible in the drill drawer for a role (unique, with ≥1 context of that role).
  const drillPersons = useMemo(() => {
    if (!drillRole) return [];
    const col = GRID_COLUMNS.find((g) => g.key === drillRole);
    if (!col) return [];
    const ctxs = usingSample ? flattenUsers(SAMPLE_USERS) : (columnState[drillRole] || EMPTY_COLUMN).ctxs;
    const byId = new Map<string, PersonCtx[]>();
    for (const c of ctxs) {
      if (c.role !== col.role || (col.paid !== null && c.paid !== col.paid)) continue;
      const list = byId.get(c.personId) || [];
      list.push(c);
      byId.set(c.personId, list);
    }
    return Array.from(byId.entries()).map(([id, list]) => ({ id, name: list[0].name, email: list[0].email, ctxs: list }));
  }, [drillRole, columnState, usingSample]);

  const drillPerson = drillPersons.find((p) => p.id === drillPersonId) || null;

  return (
    <main className="min-h-full bg-[var(--v-base-bg)] p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-[var(--v-text-primary)]">Matriz de permisos en vivo</h1>
        <p className="mb-4 text-[var(--v-text-secondary)]">Módulos × rol por contexto · resuelto en vivo por el resolver de entitlements · solo super-admin</p>

        <section className="bg-white dark:bg-[var(--v-surface-1)] rounded-xl p-5 shadow mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <p className="text-sm text-[var(--v-text-secondary)] flex-1">
              Cada celda agrega <b>todos los contextos</b> donde una persona tiene ese rol (una persona puede ser Empleado en un condominio y Propietario/Residente en otro). Cuando los contextos difieren, la celda muestra un desglose tipo <b>✅ 3 / ❌ 2</b>.
            </p>
            <button className="bg-[#36CCD8] text-white rounded px-4 py-2 ml-auto" onClick={fetchData} disabled={loading}>
              {loading ? 'Actualizando…' : '🔄 Actualizar'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Último cálculo: <b>{fmtTime(computedAt)}</b> · datos en vivo del resolver, sin encuesta automática.
            {usingSample && <span className="ml-2 rounded-full px-2 py-0.5 bg-amber-100 text-amber-700 font-semibold">Datos de ejemplo (endpoint aún no disponible)</span>}
            {Object.entries(columnState).map(([k, st]) => st.error ? (
              <span key={k} className="ml-2 rounded-full px-2 py-0.5 bg-red-50 text-red-600">{k}: sin datos</span>
            ) : null)}
          </p>
          {error && <p className="text-red-600 mt-2">{error} <button className="underline text-indigo-600" onClick={loadSample}>Ver con datos de ejemplo</button></p>}
        </section>

        <section className="bg-white dark:bg-[var(--v-surface-1)] rounded-xl p-5 shadow mb-6 overflow-x-auto">
          {loading && !usingSample ? <p className="text-sm text-gray-500">Cargando matriz efectiva…</p> : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left border-b-2">
                  <th className="sticky left-0 bg-white dark:bg-[var(--v-surface-1)] py-2 pr-4 min-w-[200px]">Módulo</th>
                  {GRID_COLUMNS.map((col) => {
                    const st = columnState[col.key] || EMPTY_COLUMN;
                    const isSuper = col.key === 'super_admin';
                    return (
                      <th key={col.key} className={`py-2 px-3 text-center cursor-pointer hover:bg-gray-50 dark:hover:bg-[var(--v-surface-2)] ${col.badge === 'Pago' ? 'bg-indigo-50/60 dark:bg-transparent' : ''}`} onClick={() => setDrillRole(col.key)}
                        title={isSuper ? 'Super-admin: acceso completo a todos los módulos por diseño (se omiten los filtros de plan).' : `Ver personas y contextos del rol ${col.label} (${col.badge ?? 'todos los planes'})`}>
                        {col.label}
                        {col.badge && (
                          <span className={`block text-[10px] font-semibold uppercase tracking-wide ${col.badge === 'Pago' ? 'text-indigo-600' : 'text-gray-400'}`}>
                            {col.badge === 'Pago' ? 'Pago' : 'Gratis'}
                          </span>
                        )}
                        <span className="block text-xs font-normal text-gray-500">
                          {st.ctxs.length} contexto(s)
                          {st.persons !== st.ctxs.length && <span className="text-[10px] text-gray-400"> · {st.persons} persona(s)</span>}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {([
                  { label: 'Funciones de Unidad (Hogar)', rows: UNIT_ROWS },
                  { label: 'Funciones de Condominio', rows: CONDO_ROWS },
                ]).map((section) => [
                  <tr key={`section-${section.label}`} className="border-t bg-gray-100 dark:bg-[var(--v-surface-2)]">
                    <td colSpan={GRID_COLUMNS.length + 1} className="py-1.5 px-3 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                      {section.label}
                    </td>
                  </tr>,
                  ...section.rows.map((item) => (
                    <tr key={item.key} className="border-t hover:bg-gray-50 dark:hover:bg-[var(--v-surface-2)]">
                      <td className="sticky left-0 bg-white dark:bg-[var(--v-surface-1)] py-2 pr-4 font-medium">{item.label}</td>
                      {GRID_COLUMNS.map((col) => {
                        const st = columnState[col.key] || EMPTY_COLUMN;
                        const isSuper = col.key === 'super_admin';
                        const cell = isSuper && st.ctxs.length > 0
                          ? ({ kind: 'single', status: 'visible' } as CellState) // full access by design
                          : columnCell(item, st.ctxs);
                        return (
                          <td key={col.key} className="text-center py-2 px-3 cursor-pointer" onClick={() => setDrillRole(col.key)}>
                            <CellGlyph cell={cell} itemLabel={item.label} roleLabel={`${col.label}${col.badge ? ` · ${col.badge}` : ''}`} />
                          </td>
                        );
                      })}
                    </tr>
                  )),
                ])}
              </tbody>
            </table>
          )}
          <p className="text-xs text-gray-500 mt-3">
            ✅ visible · ❌ oculto · 🔸N visible con límite N · — sin contextos muestreados.
            Las columnas de rol están divididas por plan (<b>Pago</b> / <b>Gratis</b>): cada contexto cae exactamente en una columna según su rol y si tiene algún plan pago (home_plus, home_ultra, condominio pago o supplier_pro).
            Celdas divididas (p. ej. <b>✅ 2 / ❌ 1</b>) = residuo de contextos que difieren dentro de la misma columna; pasa el cursor para el desglose y haz clic en la columna para ver persona por persona.
            Mensajes y Quejas y Sugerencias son siempre visibles (regla canónica). Mis Pagos fue eliminada de todos los menús (Jaime 2026-09-07): sin fila en esta matriz. Rentas y Rotación están ocultas para el rol empleado y además requieren los módulos de plan 'rentals' / 'turnover' (❌ hasta que el backend los siembre). Usuarios se rige por el gate de directorio-admin (rol): ✅ super-admin/admin/propietario, ❌ residente/empleado. Super-admin: acceso completo por diseño.
          </p>
          <p className="text-xs text-gray-600 mt-2 border-l-4 border-amber-400 pl-2 bg-amber-50 dark:bg-transparent py-1 rounded">
            <b>Nota multi-rol:</b> una persona puede tener roles distintos por contexto (p. ej. Emily es Residente de Allegra 601 y TH1, y Propietaria/Residente de Casa Tajali; Francisco es Empleado de TH1 y Propietario/Residente de Casa Prueba).
            Cada columna refleja el permiso <b>por rol en cada contexto</b>, no un rol único por persona.
            Un contexto con varios roles (p. ej. Propietario + Residente) aparece en <b>todas</b> sus columnas de rol con la misma máscara y límites; el badge de rol muestra el conjunto completo.
          </p>
        </section>
      </div>

      {/* Level 1 drawer: persons of the clicked role */}
      {drillRole && !drillPersonId && (() => {
        const roleMeta = GRID_COLUMNS.find((g) => g.key === drillRole)!;
        return (
          <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setDrillRole(null)}>
            <aside className="bg-white dark:bg-[var(--v-surface-1)] w-full max-w-lg h-full overflow-y-auto p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-[var(--v-text-primary)]">Rol: {roleMeta.label}{roleMeta.badge ? ` · ${roleMeta.badge}` : ''}</h2>
                  <p className="text-sm text-gray-500">{drillPersons.length} persona(s) · {drillPersons.reduce((n, p) => n + p.ctxs.length, 0)} contexto(s). Haz clic en una persona para ver sus contextos lado a lado.</p>
                </div>
                <button className="text-gray-500 text-xl leading-none" onClick={() => setDrillRole(null)} aria-label="Cerrar">✕</button>
              </div>
              <div className="space-y-2">
                {drillPersons.map((p) => (
                  <button key={p.id} className="w-full text-left border rounded-lg p-3 text-sm hover:bg-gray-50 dark:hover:bg-[var(--v-surface-2)]" onClick={() => setDrillPersonId(p.id)}>
                    <div className="font-semibold">{p.name} <span className="text-xs font-normal text-gray-500">{p.email}</span></div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {p.ctxs.length} contexto(s) como {roleMeta.label}{roleMeta.badge ? ` · ${roleMeta.badge}` : ''}: {p.ctxs.map((c) => `${c.contextLabel}${c.roleSet.length > 1 ? ` (${roleSetLabel(c.roleSet)})` : ''}`).join(' · ')}
                    </div>
                  </button>
                ))}
                {drillPersons.length === 0 && <p className="text-xs text-gray-400">Sin personas con este rol.</p>}
              </div>
            </aside>
          </div>
        );
      })()}

      {/* Level 2 drawer: the person's contexts side by side */}
      {drillRole && drillPerson && (() => {
        const roleMeta = GRID_COLUMNS.find((g) => g.key === drillRole)!;
        return (
          <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setDrillPersonId(null)}>
            <aside className="bg-white dark:bg-[var(--v-surface-1)] w-full max-w-2xl h-full overflow-y-auto p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-[var(--v-text-primary)]">{drillPerson.name}</h2>
                  <p className="text-sm text-gray-500">{drillPerson.email} · todos sus contextos, lado a lado (vista centrada en {roleMeta.label})</p>
                </div>
                <button className="text-gray-500 text-xl leading-none" onClick={() => setDrillPersonId(null)} aria-label="Volver">↩</button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {drillPerson.ctxs.map((c, i) => (
                  <div key={i} className={`border rounded-lg p-3 text-sm ${c.role === drillRole ? 'border-[#36CCD8] bg-cyan-50/40 dark:bg-transparent' : 'border-gray-200 opacity-90'}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="font-semibold">{c.contextLabel}</div>
                      <span className="text-[11px] rounded-full px-2 py-0.5 bg-indigo-100 text-indigo-700 font-semibold whitespace-nowrap">
                        {c.roleSet.length > 1 ? roleSetLabel(c.roleSet) : (ROLE_LABELS[c.role] || c.role)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mb-2">Fuente: {c.sourceAttribution || '—'}</div>
                    {/* F-12 (2026-09-25): payer attribution — display-only. When the
                        context's plan is covered by an ancestor condo, say WHO pays so
                        admins can see why a free-tier condo "shows paid". The context's
                        own tier stays visible in Fuente above. Backend ships
                        payerCondoId only (no name yet) → short id shown. */}
                    {c.payerInfo?.coveredByAncestor && c.payerInfo.payerCondoId && (
                      <div className="mb-2 flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11px] rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 font-semibold">
                          Pagado por condominio {String(c.payerInfo.payerCondoId).slice(0, 8)}
                        </span>
                        {c.payerInfo.payerPlan && <span className="text-[11px] text-gray-500">plan del pagador: {String(c.payerInfo.payerPlan)}</span>}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {c.modules.map((m) => <span key={m} className="text-xs rounded-full px-2 py-0.5 bg-indigo-50 text-indigo-700">{LABELS[m] || m}</span>)}
                      {c.modules.length === 0 && <span className="text-xs text-gray-400">Sin módulos efectivos</span>}
                    </div>
                    {Object.keys(c.limits).length > 0 && (
                      <div className="mt-2 text-[11px] text-gray-500">
                        Límites: {Object.entries(c.limits).map(([k, v]) => `${LABELS[k] || k}=${String(v)}`).join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </aside>
          </div>
        );
      })()}
    </main>
  );
}
